import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type ClipboardEvent,
  type KeyboardEvent,
} from 'react';
import {nanoid} from 'nanoid';
import {useAuth} from '@api/auth';
import {useAddTaskComment, useUpdateTaskComment} from '@api/tasks/hooks';
import type {Task, TaskComment, TaskParticipant} from '@api/tasks/types';
import {toast} from 'sonner';
import {taskCommentError} from './commentErrors';
import {
  reconcileMentionTokens,
  restoreCommentMentions,
  changedCommentMentions,
  removeFirstCommentMention,
  commentMentionRanges,
  TASK_COMMENT_MAX_LENGTH,
  type MentionToken,
  type MentionEditRange,
} from './mentions';

/** Черновик комментария, адресация по ID и безопасный повтор одной команды. */
export const useTaskComments = (
  task: Task | undefined,
  open: boolean,
  canComment: boolean,
  edit?: {comment: TaskComment; onSaved: () => void},
) => {
  const {currentUser} = useAuth();
  const addComment = useAddTaskComment();
  const updateComment = useUpdateTaskComment();
  const isPending = addComment.isPending || updateComment.isPending;
  const canSubmit = edit ? edit.comment.canEdit === true : canComment;
  const [commentDraft, setCommentDraft] = useState('');
  const [pendingMentions, setPendingMentions] = useState<MentionToken[]>([]);
  const [detachedMentions, setDetachedMentions] = useState<TaskParticipant[]>(
    [],
  );
  const initialComment = useRef(edit?.comment);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const commentRef = useRef<HTMLTextAreaElement>(null);
  const commentEditRange = useRef<MentionEditRange | undefined>(undefined);
  const commentCommand = useRef<{signature: string; key: string} | null>(null);
  const session = useRef(0);
  const sending = useRef(false);
  useEffect(() => {
    session.current++;
    const initial = initialComment.current;
    const restored = restoreCommentMentions(initial);
    setCommentDraft(initial?.text ?? '');
    setPendingMentions(restored.tokens);
    setDetachedMentions(restored.detached);
    setMentionQuery(null);
    commentCommand.current = null;
    commentEditRange.current = undefined;
    return () => {
      session.current++;
    };
  }, [open, task?.id]);
  const selectedMentions = [
    ...new Map(
      [...pendingMentions, ...detachedMentions].map((person) => [
        person.userSid,
        person,
      ]),
    ).values(),
  ];
  // Mentions: detect '@<query>' before caret
  const handleCommentChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const v = e.target.value;
    const editRange = commentEditRange.current;
    setPendingMentions((tokens) =>
      reconcileMentionTokens(commentDraft, v, tokens, editRange),
    );
    commentEditRange.current = undefined;
    setCommentDraft(v);
    const cursor = e.target.selectionStart ?? v.length;
    const upToCursor = v.slice(0, cursor);
    const at = upToCursor.lastIndexOf('@');
    if (at < 0) {
      setMentionQuery(null);
      return;
    }
    const after = upToCursor.slice(at + 1);
    if (/^[\wА-Яа-яЁё .\-]*$/.test(after) && !after.includes('\n')) {
      setMentionQuery(after);
    } else {
      setMentionQuery(null);
    }
  };

  const matchedManagers = useMemo(() => {
    const q = (mentionQuery ?? '').toLowerCase();
    return (task?.mentionCandidates ?? [])
      .filter(
        (m) =>
          m.displayName.toLowerCase().includes(q) &&
          !pendingMentions.some((token) => token.userSid === m.userSid) &&
          !detachedMentions.some((person) => person.userSid === m.userSid),
      )
      .slice(0, 6);
  }, [
    task?.mentionCandidates,
    mentionQuery,
    pendingMentions,
    detachedMentions,
  ]);

  const insertMention = (person: {userSid: string; displayName: string}) => {
    if (
      !canSubmit ||
      isPending ||
      !commentRef.current ||
      selectedMentions.length >= 2
    )
      return;
    const cursor = commentRef.current.selectionStart ?? commentDraft.length;
    const at =
      mentionQuery !== null
        ? commentDraft.slice(0, cursor).lastIndexOf('@')
        : cursor;
    if (at < 0) return;
    const prefix =
      mentionQuery === null && at > 0 && !/[\s([{]/u.test(commentDraft[at - 1])
        ? ' '
        : '';
    const tokenStart = at + prefix.length;
    const tokenText = `@${person.displayName}`;
    const next =
      commentDraft.slice(0, at) +
      prefix +
      tokenText +
      ' ' +
      commentDraft.slice(cursor);
    if (next.length > TASK_COMMENT_MAX_LENGTH) {
      toast.error(`Комментарий — не более ${TASK_COMMENT_MAX_LENGTH} символов`);
      return;
    }
    setPendingMentions([
      ...reconcileMentionTokens(commentDraft, next, pendingMentions, {
        start: at,
        end: cursor,
      }),
      {...person, start: tokenStart, end: tokenStart + tokenText.length},
    ]);
    setCommentDraft(next);
    setMentionQuery(null);
    requestAnimationFrame(() => {
      commentRef.current?.focus();
      commentRef.current?.setSelectionRange(
        tokenStart + tokenText.length + 1,
        tokenStart + tokenText.length + 1,
      );
    });
  };

  const sendComment = async () => {
    const text = commentDraft.trim();
    if (!text || !task || isPending || sending.current || !canSubmit) return;
    if (commentDraft.length > TASK_COMMENT_MAX_LENGTH) {
      toast.error(`Комментарий — не более ${TASK_COMMENT_MAX_LENGTH} символов`);
      return;
    }
    const validTokens = pendingMentions.filter(
      (token) =>
        commentDraft.slice(token.start, token.end) ===
          `@${token.displayName}` &&
        (initialComment.current?.mentions.includes(token.userSid) ||
          task.mentionCandidates?.some(
            (person) => person.userSid === token.userSid,
          )),
    );
    const mentions = [
      ...new Set(
        [...validTokens, ...detachedMentions].map((person) => person.userSid),
      ),
    ];
    if (mentions.length > 2) {
      toast.error('Можно упомянуть не более двух участников задачи');
      return;
    }
    const signature = JSON.stringify({taskId: task.id, text, mentions});
    if (commentCommand.current?.signature !== signature)
      commentCommand.current = {signature, key: nanoid()};
    const requestSession = session.current;
    sending.current = true;
    try {
      if (edit) {
        const changed = changedCommentMentions(
          initialComment.current?.mentions ?? [],
          mentions,
        );
        await updateComment.mutateAsync({
          taskId: task.id,
          commentId: edit.comment.id,
          input: {text, ...(changed !== undefined ? {mentions: changed} : {})},
        });
      } else
        await addComment.mutateAsync({
          taskId: task.id,
          text,
          mentions,
          author: currentUser?.displayName || 'Пользователь',
          idempotencyKey: commentCommand.current.key,
        });
      if (requestSession !== session.current) return;
      if (edit) {
        toast.success('Комментарий изменён');
        edit.onSaved();
        return;
      }
      setCommentDraft('');
      setPendingMentions([]);
      setDetachedMentions([]);
      setMentionQuery(null);
      commentCommand.current = null;
      if (mentions.length)
        toast.success(
          `Упомянуты: ${validTokens.map((token) => token.displayName).join(', ')}`,
        );
    } catch (error) {
      if (requestSession !== session.current) return;
      toast.error(
        edit
          ? taskCommentError(error, 'edit')
          : 'Комментарий не подтверждён. Повторите отправку — дубль не создастся.',
      );
    } finally {
      sending.current = false;
    }
  };

  const rememberSelection = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    commentEditRange.current = {
      start: event.currentTarget.selectionStart,
      end: event.currentTarget.selectionEnd,
    };
  };
  const onBeforeInput = (event: FormEvent<HTMLTextAreaElement>) => {
    const input = event.nativeEvent as InputEvent;
    let {selectionStart: start, selectionEnd: end} = event.currentTarget;
    if (start === end && input.inputType === 'deleteContentBackward')
      start = Math.max(0, start - 1);
    if (start === end && input.inputType === 'deleteContentForward')
      end = Math.min(commentDraft.length, end + 1);
    commentEditRange.current = {start, end};
  };
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    let {selectionStart: start, selectionEnd: end} = event.currentTarget;
    if (start === end && event.key === 'Backspace')
      start = Math.max(0, start - 1);
    if (start === end && event.key === 'Delete')
      end = Math.min(commentDraft.length, end + 1);
    commentEditRange.current = {start, end};
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      void sendComment();
    }
  };
  return {
    commentDraft,
    highlightRanges: commentMentionRanges(
      commentDraft,
      [...(task?.mentionCandidates ?? []), ...selectedMentions].map(
        (person) => person.displayName,
      ),
    ),
    selectedMentions,
    removeMention: (userSid: string) => {
      if (isPending || !canSubmit) return;
      const person = selectedMentions.find((item) => item.userSid === userSid);
      if (!person) return;
      const removed = removeFirstCommentMention(
        commentDraft,
        person.displayName,
      );
      setCommentDraft(removed.text);
      setPendingMentions((tokens) =>
        reconcileMentionTokens(
          commentDraft,
          removed.text,
          tokens,
          removed.range,
        ).filter((token) => token.userSid !== userSid),
      );
      setDetachedMentions((people) =>
        people.filter((person) => person.userSid !== userSid),
      );
      setMentionQuery(null);
      commentEditRange.current = undefined;
      if (removed.range) {
        const cursor = removed.range.start;
        requestAnimationFrame(() => {
          commentRef.current?.focus();
          commentRef.current?.setSelectionRange(cursor, cursor);
        });
      }
    },
    canSubmit,
    mentionQuery,
    closeMentions: () => setMentionQuery(null),
    matchedManagers,
    insertMention,
    sendComment,
    isPending,
    textareaProps: {
      ref: commentRef,
      value: commentDraft,
      disabled: !canSubmit || isPending,
      maxLength: TASK_COMMENT_MAX_LENGTH,
      onBeforeInput,
      onPaste: rememberSelection,
      onCut: rememberSelection,
      onChange: handleCommentChange,
      onKeyDown,
    },
  };
};
